import { createSelector } from '@reduxjs/toolkit';

import { distanceEquals, Point2D, Vector2D } from '@paragon/geometry-library';
import { assertExhaustiveSwitch, roundToIncrement } from '@paragon/utilities';

import {
  AxisSnapTarget,
  getAxisValue,
  resolveAxisSnapTargetPerpendicularRange,
  SNAP_AXES,
} from '../../library/layout-snapping/interactive-snapping';
import { InteractiveEdit } from '../slices/interactiveEditingSlice';
import {
  getActiveEditHasMoved,
  getActiveInteractiveEdit,
  getActiveMoveEditBaseShapeVertices,
  getInteractiveEditAxisSnapTargets,
} from './interactiveEditingSelectors';

export interface AlignmentGuide {
  readonly axis: 'x' | 'y';
  readonly value: number;
  readonly from: number;
  readonly to: number;
}

const GUIDE_EXTENSION = 24;

/**
 * Returns the source points to check for alignment based on the active edit type.
 * - Move edits: all vertices of all edited envelope shapes
 * - Length edits: the single endpoint being dragged
 */
function getSourcePointsForGuides(
  activeEdit: NonNullable<InteractiveEdit>,
  baseMoveEditShapeVertices: readonly Point2D[],
): readonly Point2D[] {
  switch (activeEdit.type) {
    case 'bearingEnvelopeMove': {
      const delta = Vector2D.fromOriginTo(activeEdit.currentDelta);
      return baseMoveEditShapeVertices.map((vertex) => vertex.translate(delta));
    }

    case 'bearingEnvelopeLength': {
      const point =
        activeEdit.endpoint === 'left' ? activeEdit.currentPatch.leftPoint : activeEdit.currentPatch.rightPoint;
      return point ? [Point2D.fromObject(point)] : [];
    }

    default:
      assertExhaustiveSwitch(activeEdit);
  }
}

function mergeDuplicateGuides(rawGuides: readonly AlignmentGuide[]): AlignmentGuide[] {
  const mergedGuides = new Map<string, AlignmentGuide>();

  for (const guide of rawGuides) {
    const key = `${guide.axis}:${guide.value}`;
    const existing = mergedGuides.get(key);
    if (existing) {
      mergedGuides.set(key, {
        ...existing,
        from: Math.min(existing.from, guide.from),
        to: Math.max(existing.to, guide.to),
      });
    } else {
      mergedGuides.set(key, guide);
    }
  }

  return Array.from(mergedGuides.values());
}

function pushAxisGuide(src: Point2D, target: AxisSnapTarget, into: AlignmentGuide[]): void {
  const srcOnAxis = getAxisValue(src, target.axis);
  if (!distanceEquals(srcOnAxis, target.value)) return;

  const range = resolveAxisSnapTargetPerpendicularRange(target, src);
  if (range == null) return;

  into.push({
    axis: target.axis,
    value: roundToIncrement(target.value, 1 / 16),
    from: range.lo - GUIDE_EXTENSION,
    to: range.hi + GUIDE_EXTENSION,
  });
}

export const getAlignmentGuides = createSelector(
  getActiveInteractiveEdit,
  getActiveEditHasMoved,
  getInteractiveEditAxisSnapTargets,
  getActiveMoveEditBaseShapeVertices,
  (activeEdit, hasMoved, targets, baseMoveEditShapeVertices): readonly AlignmentGuide[] => {
    if (!activeEdit || !hasMoved) return [];

    const sourcePoints = getSourcePointsForGuides(activeEdit, baseMoveEditShapeVertices);
    if (sourcePoints.length === 0 || (targets.x.length === 0 && targets.y.length === 0)) return [];

    const rawGuides: AlignmentGuide[] = [];

    for (const src of sourcePoints) {
      for (const axis of SNAP_AXES) {
        for (const target of targets[axis]) {
          pushAxisGuide(src, target, rawGuides);
        }
      }
    }

    return mergeDuplicateGuides(rawGuides);
  },
);
